import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TableCell, Table, TableHead, TableRow, TableBody, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, Box, Typography, Avatar, Grid, Paper, TextField, Button, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import TablePagination from '@mui/material/TablePagination';


const OverviewContainer = styled(Box)({
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
});

const ContentContainer = styled(Box)({
    flexGrow: 1,
});

const SectionTitle = styled(Typography)({
    color: '#12c099',
    fontWeight: 'bold',
    marginBottom: '1rem',
});

const StyledTableContainer = styled(Box)({
    backgroundColor: '#202020',
    borderRadius: '8px',
    marginTop: '1rem',
    overflowX: 'auto',
});

const CompactTableCell = styled(TableCell)({
    padding: '12px 16px',
    fontSize: '0.9rem',
    color: '#fff',
    fontWeight: 400,
});


const Employee = () => {
    const { firebaseUid } = useParams();
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [formValues, setFormValues] = useState({});
    const [organizedSessions, setOrganizedSessions] = useState([]);
    const [neighborhoodModalOpen, setNeighborhoodModalOpen] = useState(false);
    const [availableNeighborhoods, setAvailableNeighborhoods] = useState([]);
    const [page, setPage] = useState(0);
    const rowsPerPage = 5;


    useEffect(() => {
        if (firebaseUid) {
            fetchOrganizedSessions();
        }
    }, [firebaseUid]);

    useEffect(() => {
        fetchEmployee();
    }, [firebaseUid]);

    const fetchEmployee = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/employee/${firebaseUid}`);
            setEmployee(response.data);
            setFormValues(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching employee data:', error);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (neighborhoodModalOpen) {
            fetchNeighborhoods();
        }
    }, [neighborhoodModalOpen]);

    const fetchNeighborhoods = async () => {
        try {
            const address = `${employee.address?.city || employee.location?.city?.[0]}, ${employee.address?.state || employee.location?.state?.[0]}, ${employee.address?.country || employee.location?.country}`;
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/generate-neighborhoods`, { address });
            setAvailableNeighborhoods(response.data.neighborhoods || []);
        } catch (error) {
            console.error('Error fetching neighborhoods:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const fetchOrganizedSessions = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/employee/${firebaseUid}/sessions-organized`);
            setOrganizedSessions(response.data.sessions || []);
        } catch (error) {
            console.error('Error fetching sessions organized by employee:', error);
        }
    };

    const handleSave = async () => {
        try {
            await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/update-employee/${firebaseUid}`, formValues);
            setEditing(false);
            fetchEmployee(); // reload after saving changes
        } catch (error) {
            console.error('Error updating employee data:', error);
        }
    };

    const handleNeighborhoodToggle = (e, neighborhood) => {
        setFormValues((prev) => {
            const currentNeighborhoods = prev.neighborhoods || [];
            if (e.target.checked) {
                return {
                    ...prev,
                    neighborhoods: [...currentNeighborhoods, neighborhood],
                };
            } else {
                return {
                    ...prev,
                    neighborhoods: currentNeighborhoods.filter((n) => n.name !== neighborhood.name),
                };
            }
        });
    };

    const handleSaveNeighborhoods = async () => {
        try {
            await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/assign-neighborhoods/${firebaseUid}`, {
                neighborhoods: formValues.neighborhoods
            });
            setNeighborhoodModalOpen(false);
            fetchEmployee(); // Refresh
        } catch (error) {
            console.error('Error saving neighborhoods:', error);
        }
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" mt={5}><CircularProgress color="inherit" /></Box>;
    }

    if (!employee) {
        return <Typography variant="h6" color="inherit">Employee not found.</Typography>;
    }

    return (
        <OverviewContainer>
            <ContentContainer>
                <Typography variant="h4" gutterBottom>Employee Overview</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={4} md={3}>
                        <Avatar src={employee.avatarImageUrl || ''} sx={{ width: 120, height: 120 }} />
                    </Grid>
                    <Grid item xs={12} sm={8} md={9}>
                        <Typography variant="h5">{employee.name}</Typography>
                        <Typography variant="body1">Title: {employee.title}</Typography>
                        <Typography variant="body1">
                            Location: {employee.location?.city?.join(', ')}, {employee.location?.state?.join(', ')}
                        </Typography>
                        <Typography variant="body1">Email: {employee.email}</Typography>
                        <Typography variant="body1">Phone: {employee.phoneNumber}</Typography>
                        <Typography variant="body1">Sport: {employee.sport}</Typography>
                    </Grid>
                </Grid>

                <Paper sx={{ padding: '1.5rem', backgroundColor: '#202020', color: '#fff', borderRadius: '8px', marginTop: '20px' }}>
                    <SectionTitle variant="h6">Edit Employee Information</SectionTitle>
                    <Grid container spacing={3}>
                        {/* Fields */}
                        {['name', 'email', 'phoneNumber', 'title', 'sport'].map((field) => (
                            <Grid item xs={12} sm={6} key={field}>
                                <TextField
                                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                                    name={field}
                                    value={formValues[field] || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    disabled={!editing}
                                    InputProps={{ style: { color: '#fff' } }}
                                    InputLabelProps={{ style: { color: '#12c099' } }}
                                    variant="outlined"
                                    sx={{ backgroundColor: '#333' }}
                                />
                            </Grid>
                        ))}

                        {/* Assigned Neighborhoods */}
                        {/* Assigned Neighborhoods */}
                        <Grid item xs={12}>
                            <SectionTitle variant="h6">Assigned Neighborhoods</SectionTitle>
                            {employee.neighborhoods && employee.neighborhoods.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {employee.neighborhoods.map((n, index) => (
                                        <Chip
                                            key={index}
                                            label={n.name}
                                            sx={{
                                                backgroundColor: '#12c099',
                                                color: '#000',
                                                fontWeight: 'bold',
                                                borderRadius: '12px', // softened
                                                padding: '4px 10px',
                                            }}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2">No neighborhoods assigned yet.</Typography>
                            )}
                        </Grid>


                        {/* Organized Sessions */}
                        <Grid item xs={12}>
                            <SectionTitle variant="h6">Sessions Organized</SectionTitle>
                            {organizedSessions.length > 0 ? (
                                <StyledTableContainer component={Paper}>
                                    <Table sx={{ minWidth: 650 }}>
                                        <TableHead>
                                            <TableRow>
                                                <CompactTableCell>Date</CompactTableCell>
                                                <CompactTableCell>Session Name</CompactTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {organizedSessions
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((session) => (
                                                    <TableRow key={session._id}>
                                                        <CompactTableCell>{session.eventDate ? new Date(session.eventDate).toLocaleDateString() : 'N/A'}</CompactTableCell>
                                                        <CompactTableCell>
                                                            <a
                                                                href={`/session/${session._id}`}
                                                                style={{ color: '#12c099', textDecoration: 'none', fontWeight: 'bold' }}
                                                            >
                                                                {session.name || 'Unnamed Session'}
                                                            </a>
                                                        </CompactTableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                            <TablePagination
                                                rowsPerPageOptions={[]}
                                                component="div"
                                                count={organizedSessions.length}
                                                rowsPerPage={rowsPerPage}
                                                page={page}
                                                onPageChange={(e, newPage) => setPage(newPage)}
                                                sx={{
                                                    backgroundColor: 'transparent',
                                                    color: '#fff',
                                                    '& .MuiTablePagination-toolbar': {
                                                        justifyContent: 'center',
                                                    },
                                                }}
                                            />
                                        </Box>

                                    </Table>
                                </StyledTableContainer>
                            ) : (
                                <Typography variant="body2">No sessions organized yet.</Typography>
                            )}
                        </Grid>


                        {/* Buttons */}
                        <Grid item xs={12}>
                            {editing ? (
                                <Button variant="contained" color="primary" onClick={handleSave}>
                                    Save Changes
                                </Button>
                            ) : (
                                <Button variant="contained" color="primary" onClick={() => setEditing(true)}>
                                    Edit Employee
                                </Button>
                            )}
                            <Button variant="outlined" color="secondary" onClick={() => setNeighborhoodModalOpen(true)} sx={{ ml: 2 }}>
                                Assign Neighborhoods
                            </Button>
                        </Grid>
                    </Grid>

                </Paper>



                {/* Neighborhood Assign Modal */}
                <Dialog open={neighborhoodModalOpen} onClose={() => setNeighborhoodModalOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Assign Neighborhoods</DialogTitle>
                    <DialogContent>
                        {availableNeighborhoods.length > 0 ? (
                            availableNeighborhoods.map((neighborhood, index) => (
                                <FormControlLabel
                                    key={index}
                                    control={
                                        <Checkbox
                                            checked={formValues.neighborhoods?.some(n => n.name === neighborhood.name)}
                                            onChange={(e) => handleNeighborhoodToggle(e, neighborhood)}
                                        />
                                    }
                                    label={neighborhood.name}
                                />
                            ))
                        ) : (
                            <Typography>Loading neighborhoods...</Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setNeighborhoodModalOpen(false)}>Cancel</Button>
                        <Button onClick={handleSaveNeighborhoods} variant="contained" color="primary">
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </ContentContainer>

        </OverviewContainer>
    );
};

export default Employee;
